/**
 * Chart tokens for the receivables dashboard.
 *
 * Every value here was checked with the data-viz palette validator against
 * the white card surface (#FFFFFF) rather than picked by eye:
 *
 *  - AGING_SCALE — "Current" is a cool hue (blue) and the four overdue
 *    buckets are a single-hue ordinal ramp. Validated: the ramp passes the
 *    ordinal gate (monotone lightness, >=0.06 step gaps, light end 2.24:1
 *    vs surface, 10 deg hue spread); the one cross-family pair,
 *    Current vs 1-30, passes CVD at dE 28.0 (protan).
 *    The previous green->yellow->orange->orange->red ramp failed on all
 *    counts: non-monotone lightness, two oranges 0.059 apart, and yellow at
 *    1.92:1 was effectively invisible on white.
 *  - SERIES — the two-series categorical pair (invoiced vs collected)
 *    passes every check, worst adjacent CVD dE 23.1.
 *
 * OVERDUE_LIGHT (#EE9A4D) sits at 2.24:1, under the 3:1 bar, so anything
 * using it must ship visible labels or a table view — both are provided by
 * the components that consume these tokens.
 */

export const AGING_SCALE = {
  current: "#2a78d6",
  d1_30: "#EE9A4D",
  d31_60: "#D4762A",
  d61_90: "#A85615",
  d90_plus: "#733609",
} as const;

/** Aging colors keyed by the bucket labels the API returns. */
export const AGING_BUCKET_COLORS: Record<string, string> = {
  Current: AGING_SCALE.current,
  "0-30 days": AGING_SCALE.d1_30,
  "1-30 days": AGING_SCALE.d1_30,
  "31-60 days": AGING_SCALE.d31_60,
  "61-90 days": AGING_SCALE.d61_90,
  "90+ days": AGING_SCALE.d90_plus,
  "91 and Over": AGING_SCALE.d90_plus,
};

/** Categorical series slots — assign in fixed order, never cycled. */
export const SERIES = {
  primary: "#2a78d6",
  secondary: "#1baf7a",
} as const;

/** Status tokens — reserved for state, always paired with an icon or label. */
export const STATUS = {
  good: "#0ca30c",
  warning: "#fab219",
  critical: "#d03b3b",
} as const;

/** Chart chrome — recessive by design. */
export const CHROME = {
  surface: "#FFFFFF",
  gridline: "#EDEDEA",
  axis: "#C9C8C2",
  textPrimary: "#111827",
  textSecondary: "#52514E",
  textMuted: "#898781",
} as const;

/** Shared mark specs, so every chart draws the same weight. */
export const MARKS = {
  lineWidth: 2,
  markerRadius: 4,
  activeMarkerRadius: 5,
  surfaceRingWidth: 2,
  areaOpacity: 0.1,
  maxBarThickness: 24,
  barRadius: [4, 4, 0, 0] as [number, number, number, number],
  barRadiusHorizontal: [0, 4, 4, 0] as [number, number, number, number],
} as const;

export function agingColorFor(bucketName: string): string {
  return AGING_BUCKET_COLORS[bucketName] ?? AGING_SCALE.d31_60;
}
